import React, { useEffect, useState } from 'react';
import api from '../api';
import {
  IoTrophyOutline,
  IoBarbellOutline,
  IoCalendarOutline,
  IoTrendingUpOutline,
  IoRestaurantOutline,
  IoFlameOutline,
  IoStarOutline,
  IoShieldOutline,
  IoLockClosedOutline,
} from 'react-icons/io5';

const ICONS = {
  dumbbell: IoBarbellOutline,
  calendar: IoCalendarOutline,
  chart: IoTrendingUpOutline,
  nutrition: IoRestaurantOutline,
  trophy: IoTrophyOutline,
  barbell: IoBarbellOutline,
  star: IoStarOutline,
  flame: IoFlameOutline,
  shield: IoShieldOutline,
};

const CONDITION_LABELS = {
  workout_count: (v) => `${v} entrenos registrados`,
  progress_count: (v) => `${v} mediciones de progreso`,
  nutrition_assigned: () => 'Plan de nutrición asignado',
  program_assigned: () => 'Programa de entreno asignado',
  manual: () => 'Otorgado por tu coach',
};

const Achievements = () => {
  const [allRewards, setAllRewards] = useState([]);
  const [earned, setEarned] = useState(new Set());
  const [earnedAt, setEarnedAt] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [allRes, myRes] = await Promise.all([
          api.get('/rewards'),
          api.get('/me/rewards'),
        ]);
        setAllRewards(allRes.data || []);

        const earnedSet = new Set();
        const atMap = {};
        (myRes.data || []).forEach((ur) => {
          earnedSet.add(ur.reward.id);
          atMap[ur.reward.id] = ur.earned_at;
        });
        setEarned(earnedSet);
        setEarnedAt(atMap);
      } catch {
        // silent fail to keep UI usable
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up">
        <div className="skeleton h-10 w-64 rounded mb-3" />
        <div className="skeleton h-4 w-80 rounded mb-8" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="glass-panel rounded-xl p-5">
              <div className="skeleton h-6 w-3/5 rounded mb-3" />
              <div className="skeleton h-4 w-full rounded mb-2" />
              <div className="skeleton h-4 w-2/3 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const earnedCount = allRewards.filter((r) => earned.has(r.id)).length;

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <IoTrophyOutline size={28} className="text-yellow-400" />
          <h1 className="text-3xl md:text-4xl font-bold">Mis Logros</h1>
        </div>
        <p className="text-gray-400 mt-1">
          {earnedCount} de {allRewards.length} logros desbloqueados - sigue adelante
        </p>
      </div>

      <div className="w-full bg-white/10 rounded-full h-2 mb-8">
        <div
          className="bg-yellow-500 h-2 rounded-full transition-all duration-700"
          style={{ width: `${allRewards.length ? (earnedCount / allRewards.length) * 100 : 0}%` }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {allRewards.map((reward) => {
          const isEarned = earned.has(reward.id);
          const RewardIcon = ICONS[reward.icon] || IoTrophyOutline;
          const condLabel = reward.condition_type
            ? (CONDITION_LABELS[reward.condition_type]?.(reward.condition_value) ?? '')
            : '';

          return (
            <div
              key={reward.id}
              className={`glass-panel rounded-xl p-5 transition-all duration-300 ${
                isEarned
                  ? 'border-yellow-500/40 hover:-translate-y-1 hover:border-yellow-400/60'
                  : 'opacity-50 grayscale'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isEarned
                      ? 'bg-yellow-600/20 border border-yellow-500'
                      : 'bg-white/5 border border-white/15'
                  }`}
                >
                  {isEarned
                    ? <RewardIcon size={22} className="text-yellow-400" />
                    : <IoLockClosedOutline size={18} className="text-gray-500" />}
                </div>

                <div className="flex-1 min-w-0">
                  <h2 className={`text-base font-bold ${isEarned ? 'text-white' : 'text-gray-400'}`}>
                    {reward.name}
                  </h2>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{reward.description}</p>

                  {condLabel && !isEarned && (
                    <p className="text-xs text-gray-600 mt-2 italic">Meta: {condLabel}</p>
                  )}

                  {isEarned && earnedAt[reward.id] && (
                    <p className="text-xs text-yellow-600 mt-2">
                      {new Date(earnedAt[reward.id]).toLocaleDateString('es-ES', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Achievements;
